import { useState, useCallback, useRef, useEffect } from 'react';

const MIN_WIDTH_PX = 40;
const MAX_WIDTH_PX = 800;
const PERSIST_DEBOUNCE_MS = 200;

function parseWidthToPx(value: string, fallbackPx: number = 100): number {
  if (!value) return fallbackPx;
  const trimmed = value.trim();
  if (trimmed.endsWith('px')) {
    const n = parseInt(trimmed.slice(0, -2), 10);
    return Number.isFinite(n) ? n : fallbackPx;
  }
  if (trimmed.endsWith('%')) {
    const pct = parseInt(trimmed.slice(0, -1), 10);
    if (Number.isFinite(pct)) {
      return Math.round((pct / 100) * 1200);
    }
  }
  return fallbackPx;
}

export interface UseResizableColumnsOptions {
  defaultWidths?: Record<string, string>;
  storageKey?: string;
  minWidthPx?: number;
  maxWidthPx?: number;
}

export function useResizableColumns(
  columnIds: string[],
  options: UseResizableColumnsOptions = {}
) {
  const {
    defaultWidths = {},
    minWidthPx = MIN_WIDTH_PX,
    maxWidthPx = MAX_WIDTH_PX,
  } = options;
  // V2-027 / TBL-18: widths are remembered for the sign-in (sessionStorage, `wrap.` prefix, cleared
  // at sign-out with the rest of the user's state), per table, whether or not a key is given.
  const storageKey = `wrap.columns:${options.storageKey ?? `${typeof window !== 'undefined' ? window.location.pathname : ''}:${columnIds.join('|')}`}`;

  const initialWidths = useRef<Record<string, number>>({});
  if (Object.keys(initialWidths.current).length === 0) {
    for (const id of columnIds) {
      const def = defaultWidths[id];
      initialWidths.current[id] = def ? parseWidthToPx(def) : 100;
    }
    if (typeof sessionStorage !== 'undefined') {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (raw) {
          const saved = JSON.parse(raw) as Record<string, number>;
          for (const id of columnIds) {
            if (typeof saved[id] === 'number' && saved[id] >= minWidthPx) {
              initialWidths.current[id] = Math.min(saved[id], maxWidthPx);
            }
          }
        }
      } catch {
        // ignore
      }
    }
  }

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => ({ ...initialWidths.current }));
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const columnWidthsRef = useRef<Record<string, number>>(columnWidths);

  useEffect(() => {
    columnWidthsRef.current = columnWidths;
  }, [columnWidths]);

  const setColumnWidth = useCallback(
    (id: string, widthPx: number) => {
      const clamped = Math.min(maxWidthPx, Math.max(minWidthPx, widthPx));
      setColumnWidths((prev) => ({ ...prev, [id]: clamped }));
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
      persistTimeoutRef.current = setTimeout(() => {
        persistTimeoutRef.current = null;
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(columnWidthsRef.current));
        } catch {
          // ignore
        }
      }, PERSIST_DEBOUNCE_MS);
    },
    [storageKey, minWidthPx, maxWidthPx]
  );

  const getColumnWidth = useCallback(
    (id: string): string => {
      const px = columnWidths[id] ?? initialWidths.current[id] ?? 100;
      return `${px}px`;
    },
    [columnWidths]
  );

  const getColumnWidthPx = useCallback(
    (id: string): number => {
      return columnWidths[id] ?? initialWidths.current[id] ?? 100;
    },
    [columnWidths]
  );

  const resizeStateRef = useRef<{
    columnId: string;
    startX: number;
    startWidthPx: number;
  } | null>(null);

  const startResize = useCallback(
    (columnId: string, startX: number, startWidthPx: number) => {
      resizeStateRef.current = { columnId, startX, startWidthPx };
      const onMove = (e: MouseEvent) => {
        const state = resizeStateRef.current;
        if (!state || state.columnId !== columnId) return;
        const delta = e.clientX - state.startX;
        const newWidth = Math.min(
          maxWidthPx,
          Math.max(minWidthPx, state.startWidthPx + delta)
        );
        setColumnWidth(columnId, newWidth);
      };
      const onUp = () => {
        resizeStateRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        // Prevent the synthetic click that fires after drag from activating the column header under the cursor
        const preventClick = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          document.removeEventListener('click', preventClick, true);
        };
        document.addEventListener('click', preventClick, true);
        setTimeout(() => document.removeEventListener('click', preventClick, true), 100);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    [setColumnWidth, minWidthPx, maxWidthPx]
  );

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    };
  }, []);

  return {
    getColumnWidth,
    getColumnWidthPx,
    setColumnWidth,
    startResize,
  };
}
